// @ts-check
// Galeria dos bots prontos (/service/bots/templates; contrato de bots, seções 16 e 22.1): as categorias Captação,
// Atendimento e Relacionamento, um cartão por modelo com a descrição, a prévia somente leitura no canvas e "Usar", que
// pede o nome, o número de WhatsApp e os departamentos (como as escolhas dos fluxos-modelo) e abre o editor do bot novo.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { navigate } from '../../app/router.js';
import { createBot, listBotTemplates } from '../../data/bots.js';
import { Button } from '../../ui/button.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { SegmentedControl } from '../../ui/segmented-control.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { BOT_LIST_PATH, botEditorHref } from '../../lib/bot-list.js';
import { botStepNumbers } from '../../lib/bot-editor-state.js';
import { computeBotEditorProblems, botProblemsByNode } from '../../lib/bot-validation.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { FlowDialog } from '../flows/flow-dialog.js';
import { FlowField } from '../flows/flow-ui.js';
import { BotCanvas } from './bot-canvas.js';
import { BotMembersField, BotSelectField, useFieldId } from './bot-form-fields.js';
import { handoffTeams, memberChoices, useBotLookupData, useBotSummaryLookups } from './bot-lookups.js';
import { BotPurposeChip } from './bot-ui.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/bots.js').BotTemplateItem} BotTemplateItem
 * @typedef {import('./bot-lookups.js').BotLookupData} BotLookupData
 */

const CATEGORIES = Object.freeze(['ALL', 'CAPTURE', 'SERVICE', 'RELATIONSHIP']);

export function BotTemplatesPage() {
  const [templates, setTemplates] = useState(/** @type {BotTemplateItem[] | null} */ (null));
  const [loadError, setLoadError] = useState(false);
  const [category, setCategory] = useState('ALL');
  const [preview, setPreview] = useState(/** @type {BotTemplateItem | null} */ (null));
  const [using, setUsing] = useState(/** @type {BotTemplateItem | null} */ (null));
  const lookupData = useBotLookupData();
  const load = () => {
    setLoadError(false);
    listBotTemplates().then(setTemplates).catch(() => setLoadError(true));
  };
  useEffect(() => {
    document.title = t('bots.templates.title');
    load();
  }, []);
  const visible = (templates ?? []).filter((template) => category === 'ALL' || template.category === category);
  const header = html`<${PageHeader} icon="IconLayoutGrid" title=${t('bots.templates.title')}
    breadcrumb=${html`<button type="button" class="flow-back-link" onClick=${() => navigate(BOT_LIST_PATH)}><${Icon} name="IconChevronLeft" size=${16} /><span>${t('bots.list.title')}</span></button>`} />`;
  return html`<${PageCard} className="flows-page bots-page bot-templates-page" header=${header}>
    <div class="bot-templates" data-testid="bot-templates-page">
      <p class="bot-templates-intro">${t('bots.templates.intro')}</p>
      <${SegmentedControl} label=${t('bots.templates.categoriesLabel')} value=${category}
        options=${CATEGORIES.map((value) => ({ value, label: t(`bots.templates.categories.${value}`) }))} onChange=${setCategory} />
      ${loadError
        ? html`<div class="flows-state"><${EmptyState} icon="IconAlertTriangle" title=${t('bots.templates.loadError')}
            action=${html`<${Button} title=${t('bots.common.retry')} variant="secondary" size="small" onClick=${load} />`} /></div>`
        : templates === null
          ? html`<div class="bot-template-grid" aria-busy="true">${[0, 1, 2, 3].map((key) => html`<${Skeleton} key=${key} height=${180} radius=${8} />`)}</div>`
          : html`<ul class="bot-template-grid">
              ${visible.map((template) => html`<li key=${template.key} class="bot-template-card" data-testid=${`bot-template-${template.key}`}>
                <span class="bot-template-icon" data-category=${template.category} aria-hidden="true"><${Icon} name=${template.icon} size=${20} /></span>
                <h2 class="bot-template-name">${t(template.nameKey)}</h2>
                <p class="bot-template-description">${t(template.descriptionKey)}</p>
                <div class="bot-template-meta">
                  <span class="bot-template-category">${t(`bots.templates.categories.${template.category}`)}</span>
                  <${BotPurposeChip} purpose=${template.purpose} />
                </div>
                <div class="bot-template-actions">
                  <${Button} title=${t('bots.templates.preview')} icon="IconEye" variant="secondary" size="small" onClick=${() => setPreview(template)} />
                  <${Button} title=${t('bots.templates.use')} accent="blue" size="small" onClick=${() => setUsing(template)} data-testid=${`bot-template-use-${template.key}`} />
                </div>
              </li>`)}
            </ul>`}
    </div>
    ${preview ? html`<${BotTemplatePreviewDialog} template=${preview} lookupData=${lookupData} onClose=${() => setPreview(null)} onUse=${() => {
      setUsing(preview);
      setPreview(null);
    }} />` : null}
    ${using ? html`<${BotTemplateUseDialog} template=${using} lookupData=${lookupData} onClose=${() => setUsing(null)} />` : null}
  <//>`;
}

/**
 * Prévia somente leitura do modelo no canvas.
 * @param {{ template: BotTemplateItem, lookupData: BotLookupData, onClose: () => void, onUse: () => void }} props
 */
function BotTemplatePreviewDialog({ template, lookupData, onClose, onUse }) {
  const [focusTarget, setFocusTarget] = useState('entry');
  const [zoom, setZoom] = useState(0.8);
  const definition = template.definition;
  const lookups = useBotSummaryLookups(lookupData, definition);
  const problems = computeBotEditorProblems(definition, { purpose: definition.purpose });
  return html`<${FlowDialog} title=${t(template.nameKey)} subtitle=${t(template.descriptionKey)} size="wide" onClose=${onClose} testId="bot-template-preview"
    footer=${html`<${Button} title=${t('bots.panel.close')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${t('bots.templates.use')} accent="blue" size="small" onClick=${onUse} />`}>
    <div class="bot-template-preview-canvas">
      <${BotCanvas} definition=${definition} lookups=${lookups} lookupData=${lookupData} problems=${problems} problemsByNode=${botProblemsByNode([])}
        stepNumbers=${botStepNumbers(definition)} selectedNodeId=${null} focusTarget=${focusTarget} onFocusTarget=${setFocusTarget} zoom=${zoom} onZoom=${setZoom}
        showCounts=${false} canShowCounts=${false} onToggleCounts=${() => undefined} counts=${null} isReadOnly ariaLabel=${t('bots.canvas.treeLabel', { name: t(template.nameKey) })} />
    </div>
  <//>`;
}

/**
 * "Usar": nome e as escolhas do modelo, no formato do motor (`choices` do POST /rest/bots): número, departamentos,
 * subbots, membros e página de agendamento, cada um pela chave da escolha.
 * @param {{ template: BotTemplateItem, lookupData: BotLookupData, onClose: () => void }} props
 */
function BotTemplateUseDialog({ template, lookupData, onClose }) {
  const [name, setName] = useState(() => t(template.nameKey));
  const channels = lookupData.channels ?? [];
  const teams = handoffTeams(lookupData.teams);
  const [channelId, setChannelId] = useState(/** @type {string | null} */ (null));
  const [teamIds, setTeamIds] = useState(/** @type {Record<string, string | null>} */ ({}));
  const [botIds, setBotIds] = useState(/** @type {Record<string, string | null>} */ ({}));
  const [bookingPageIds, setBookingPageIds] = useState(/** @type {Record<string, string | null>} */ ({}));
  const [memberIds, setMemberIds] = useState(/** @type {Record<string, string[]>} */ ({}));
  const [isBusy, setIsBusy] = useState(false);
  const nameId = useFieldId('bot-template-name');
  const needsChannel = template.choices.some((choice) => choice.kind === 'CHANNEL');
  const choicesOf = (/** @type {string} */ kind) => template.choices.filter((choice) => choice.kind === kind);
  const teamChoices = choicesOf('TEAM');
  /** @param {Record<string, string | null>} values @returns {Record<string, string>} */
  const filled = (values) => /** @type {Record<string, string>} */ (Object.fromEntries(Object.entries(values).filter(([, value]) => value)));
  const selectedChannel = channelId ?? (channels.length === 1 ? channels[0].id : null);
  const submit = async () => {
    if (!name.trim() || isBusy) return;
    setIsBusy(true);
    try {
      const bot = await createBot({
        name: name.trim(), templateKey: template.key,
        choices: {
          channelIds: needsChannel && selectedChannel ? [selectedChannel] : [], teamIds: filled(teamIds), botIds: filled(botIds), bookingPageIds: filled(bookingPageIds),
          memberIds: Object.fromEntries(Object.entries(memberIds).filter(([, ids]) => ids.length > 0)),
        },
      });
      navigate(botEditorHref(bot.id));
    } catch (error) {
      showErrorSnackbar(error);
      setIsBusy(false);
    }
  };
  return html`<${FlowDialog} title=${t('bots.templates.useTitle', { name: t(template.nameKey) })} size="small" onClose=${onClose} testId="bot-template-use" onSubmit=${submit}
    footer=${html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${t('bots.templates.create')} accent="blue" size="small" type="submit" disabled=${!name.trim()} isLoading=${isBusy} onClick=${submit}
        data-testid="bot-template-create" />`}>
    <${FlowField} label=${t('bots.nameDialog.name')} htmlFor=${nameId}>
      <input id=${nameId} class="text-input" value=${name} data-autofocus onInput=${(/** @type {InputEvent} */ event) => setName(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
    <//>
    ${needsChannel
      ? html`<${BotSelectField} label=${t('bots.templates.choices.channel')} value=${selectedChannel} placeholder=${t('bots.templates.chooseLater')}
          options=${channels.map((channel) => ({ value: channel.id, label: channel.label }))} onChange=${setChannelId} hint=${channels.length === 0 ? t('bots.triggers.noChannels') : null} />`
      : null}
    ${teamChoices.map((choice) => html`<${BotSelectField} key=${choice.key} label=${t(choice.labelKey)} value=${teamIds[choice.key] ?? null} placeholder=${t('bots.templates.chooseLater')}
      options=${teams.map((team) => ({ value: team.id, label: team.name }))} onChange=${(value) => setTeamIds({ ...teamIds, [choice.key]: value })} />`)}
    ${choicesOf('BOT').map((choice) => html`<${BotSelectField} key=${choice.key} label=${t(choice.labelKey)} value=${botIds[choice.key] ?? null} placeholder=${t('bots.templates.chooseLater')}
      options=${(lookupData.bots ?? []).map((bot) => ({ value: bot.id, label: bot.name }))} onChange=${(value) => setBotIds({ ...botIds, [choice.key]: value })} />`)}
    ${choicesOf('BOOKING_PAGE').map((choice) => html`<${BotSelectField} key=${choice.key} label=${t(choice.labelKey)} value=${bookingPageIds[choice.key] ?? null}
      placeholder=${t('bots.templates.chooseLater')} options=${(lookupData.bookingPages ?? []).filter((page) => page.isActive).map((page) => ({ value: page.id, label: page.name }))}
      onChange=${(value) => setBookingPageIds({ ...bookingPageIds, [choice.key]: value })} />`)}
    ${choicesOf('MEMBERS').map((choice) => html`<${BotMembersField} key=${choice.key} label=${t(choice.labelKey)} members=${memberChoices()} selected=${memberIds[choice.key] ?? []}
      onChange=${(/** @type {string[]} */ ids) => setMemberIds({ ...memberIds, [choice.key]: ids })} />`)}
    <p class="flow-field-hint">${t('bots.templates.useHint')}</p>
  <//>`;
}
